import {
  Divider,
  Form,
  NumericFormatField,
  SubmitButton,
  Text,
  TextAreaInputField,
} from '@/components';
import { useProject, useUpdateRemark } from '@/features/project/_hooks/project';
import { useForm, useId } from '@/hooks';
import { useMessage } from '@/providers';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BiayaTenagaModel,
  type IndonesiaData,
} from '../../../_models/indonesia-data';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';

type FormValues = NonNullable<IndonesiaData['biayaTenaga']>[number];

type Props = {
  fiscalYear: number;
  initialValues?: Partial<FormValues>;
  isEditing?: boolean;
};

export const BelanjaForm: FC<Props> = ({
  fiscalYear,
  initialValues,
  isEditing,
}) => {
  const { setDrawerState } = useProjectDetailDrawerState();
  const { openMessage } = useMessage();
  const { mutateAsync: updateRemarkAsync } = useUpdateRemark();
  const { t } = useTranslation();
  const { id } = useId();
  const { data: project } = useProject(id);
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: {
      ...initialValues,
      id: initialValues ? initialValues.id : crypto.randomUUID(),
    },
    resolver: zodResolver(BiayaTenagaModel),
  });

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(async (values) => {
        const indonesiaData = project?.indonesiaData;
        if (!indonesiaData) {
          return;
        }

        const biayaTenaga = indonesiaData.biayaTenaga ?? [];

        const updateValues = isEditing
          ? biayaTenaga.map((v) => (v.id === values.id ? values : v))
          : [...biayaTenaga, values];

        try {
          await updateRemarkAsync({
            id,
            indonesiaData: {
              ...indonesiaData,
              biayaTenaga: updateValues,
            },
          });
          openMessage(
            'success',
            isEditing ? t('message-update-completed') : t('add-completed'),
          );
          setDrawerState({ open: false });
        } catch {
          openMessage(
            'error',
            isEditing ? t('message-update-failed') : t('message-update-failed'),
          );
        }
      })}
    >
      <TextAreaInputField
        name="name"
        label={t('biaya')}
        control={control}
        errors={errors}
      />
      <Text variant="h4" text={fiscalYear} />

      <NumericFormatField
        name="currentYearOrg"
        control={control}
        label={t('org')}
        errors={errors}
      />
      <NumericFormatField
        name="currentYearRp"
        control={control}
        label={t('rp')}
        errors={errors}
        disabled={false}
      />
      <Divider />

      <Text variant="h4" text={fiscalYear - 1} />

      <NumericFormatField
        name="previousYearOrg"
        control={control}
        label={t('org')}
        errors={errors}
      />
      <NumericFormatField
        name="previousYearRp"
        control={control}
        label={t('rp')}
        errors={errors}
        disabled={false}
      />
      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
